import { useGlobalContentStore } from '~/store/globalContent';

export function roundPrice(price: number): number {
  const globalContent = useGlobalContentStore();
  if (globalContent.getIsB2b) { return price; }
  return Math.ceil(price / 0.05) * 0.05;
}

export function formatPrice(price: number, decimals: number = 2): string {
  const globalContent = useGlobalContentStore();
  if (globalContent.getIsB2b) { return String(price); }

  if (price % 1 === 0) {
    return price.toFixed(0);
  }
  return price.toFixed(decimals);
}

/**
 * Normalises a Norce VAT rate to percent: 1.255 (multiplier), 0.255 (fraction) or 25.5 (percent) all become 25.5.
 */
function vatRateToPercent(rate: number): number | null {
  if (!rate || Number.isNaN(rate)) { return null; }
  if (rate > 1 && rate < 2) { return (rate - 1) * 100; }
  if (rate < 1) { return rate * 100; }
  return rate;
}

/**
 * VAT rate in percent for a basket, formatted for the current market locale with up to one decimal
 * (e.g. Finland's 25.5%). Uses the items' own VAT rate when they all share one, since the summary's
 * VAT amount is rounded to cents and would skew the rate on small totals; otherwise derives it from
 * the summary amounts.
 */
export function formatVatPercent(
  vat: unknown,
  priceBeforeVat: unknown,
  items: Array<{ priceInfo?: { vatRate?: number } | null }> = []
): string | null {
  const itemRates = [...new Set(
    items
      .map(item => vatRateToPercent(Number(item.priceInfo?.vatRate)))
      .filter((rate): rate is number => rate !== null)
      .map(rate => Math.round(rate * 10) / 10)
  )];

  let percent: number | null = itemRates.length === 1 ? itemRates[0] ?? null : null;

  if (percent === null) {
    const vatAmount = Number(vat);
    const beforeVat = Number(priceBeforeVat);
    if (!beforeVat || Number.isNaN(vatAmount) || Number.isNaN(beforeVat)) { return null; }
    percent = Math.round((vatAmount / beforeVat) * 1000) / 10;
  }

  const locale = useGlobalContentStore().getLocale || undefined;

  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(percent);
}
